import { List, Map } from 'immutable';

import { isOnlineResourcePreview } from '®shared/bibliographic-resource';
import {
  RequestCourseReserveModalActionTypes,
  RequestCourseReserveModalActions,
} from '®shared/container-structure/edit/request-course-reserve-modal/request-course-reserve-modal.actions';
import {
  CompleteRequestDigitisationAction,
  RequestDigitisationActionTypes,
} from '®shared/container-structure/edit/request-digitisation';
import {
  WithdrawCourseReserveModalActionTypes,
  WithdrawCourseReserveModalActions,
} from '®shared/container-structure/edit/withdraw-course-reserve-modal/withdraw-course-reserve-modal.actions';
import {
  ContainerEditActionTypes,
  ContainerEditActions,
} from '®shared/container-structure/state/container-edit.actions';
import {
  ContainerActionTypes,
  ContainerActions,
} from '®shared/container-structure/state/container.actions';
import {
  DigitisationStatus,
  DigitisedContent,
} from '®shared/list-item/digitisation/digitised-content';
import {
  CitationActionTypes,
  CitationActions,
} from '®shared/list-item/store/citations.actions';
import {
  LIST_ITEMS_INITIAL_STATE,
  ListItemsState,
} from '®shared/list-item/store/list-items.state';
import {
  Citation,
  CitationsByItem,
  EditableItem,
  PageableItems,
} from '®shared/reading-list/models';

import { ListItemActionTypes, ListItemActions } from './list-items.actions';

function extractListItemsById(
  items: List<EditableItem>,
): Map<string, EditableItem> {
  return items
    .groupBy(item => item.id)
    .toMap()
    .map(value => value.last());
}

function extractDefaultAndNamedCitationsByStyle(
  itemsById: Map<string, EditableItem>,
): Map<string, Map<string, Citation>> {
  const firstItemWithCitation = itemsById.find(item =>
    item.citation ? true : false,
  );

  if (
    firstItemWithCitation &&
    firstItemWithCitation.citation &&
    firstItemWithCitation.citation.style
  ) {
    const citationsByItemId = itemsById
      .map(value => value.citation)
      .filter(citation => (citation ? true : false));

    return Map({
      [firstItemWithCitation.citation.style]: citationsByItemId,
    });
  }
  return Map();
}

export function listItemsReducer(
  state: ListItemsState = LIST_ITEMS_INITIAL_STATE,
  action:
    | ListItemActions
    | CitationActions
    | ContainerActions
    | ContainerEditActions
    | CompleteRequestDigitisationAction
    | RequestCourseReserveModalActions
    | WithdrawCourseReserveModalActions,
): ListItemsState {
  switch (action.type) {
    case CitationActionTypes.CITATIONS_LOADED: {
      const citationsByItem: CitationsByItem = action.payload;

      if (citationsByItem.isEmpty()) {
        return state;
      }

      const firstCitationWithStyle = citationsByItem.find(citation =>
        citation && citation.style ? true : false,
      );

      if (!firstCitationWithStyle) {
        return state;
      }

      const newCitationsByStyle = Map({
        [firstCitationWithStyle.style]: citationsByItem.filter(citation =>
          citation ? true : false,
        ),
      });

      return {
        ...state,
        citationsByStyle: state.citationsByStyle.merge(newCitationsByStyle),
      };
    }

    case CitationActionTypes.CITATION_STYLE_CHANGED: {
      return {
        ...state,
        currentCitationStyleId: action.payload.styleId,
      };
    }

    case ListItemActionTypes.ITEMS_LOADED: {
      const { items } = action.payload as PageableItems;
      const itemsList = List(items);

      const newItemsById = extractListItemsById(itemsList);
      const defaultAndNamedCitationsByStyle =
        extractDefaultAndNamedCitationsByStyle(newItemsById);

      return {
        ...state,
        listItemsById: state.listItemsById.merge(newItemsById),
        citationsByStyle: state.citationsByStyle.mergeDeep(
          defaultAndNamedCitationsByStyle,
        ),
      };
    }

    case ListItemActionTypes.EXPAND_ITEM: {
      return {
        ...state,
        expandedItemId: action.payload.itemId,
      };
    }

    case ListItemActionTypes.COLLAPSE_ITEM: {
      return {
        ...state,
        expandedItemId: null,
      };
    }

    case ListItemActionTypes.FILTER_ITEMS_BY_TEXT_FILTER: {
      const { filterText } = action.payload;
      const textFilter =
        filterText && filterText.trim().length ? filterText.trim() : undefined;
      return {
        ...state,
        textFilter,
      };
    }

    case ListItemActionTypes.FILTER_ITEMS_BY_QUICK_FILTER: {
      const { quickFilter } = action.payload;
      return {
        ...state,
        quickFilter,
      };
    }

    case ListItemActionTypes.FILTER_ITEMS_BY_RESOURCE_TYPE_FILTER: {
      const { resourceTypeFilter } = action.payload;
      return {
        ...state,
        resourceTypeFilter,
      };
    }

    case ContainerActionTypes.CONTAINER_INITIALISED: {
      return {
        ...state,
        expandedItemId: null,
        textFilter: undefined,
        quickFilter: undefined,
        resourceTypeFilter: undefined,
      };
    }

    case ContainerEditActionTypes.CREATE_NOTE_ITEM: {
      const item: EditableItem = {
        id: action.payload.id,
        type: 'items',
        student_note: '',
      };
      const newItemById = Map({ [item.id]: item });

      return {
        ...state,
        listItemsById: state.listItemsById.merge(newItemById),
      };
    }

    case ContainerEditActionTypes.CONFIRM_CREATE_NOTE_ITEM:
    case ContainerEditActionTypes.CONFIRM_UPDATE_NOTE_ITEM: {
      const item: EditableItem = {
        id: action.payload.id,
        type: 'items',
        student_note: action.payload.student_note,
      };
      const updatedItemById = Map({ [item.id]: item });

      return {
        ...state,
        listItemsById: state.listItemsById.merge(updatedItemById),
      };
    }

    case ContainerEditActionTypes.CANCEL_CREATE_NOTE_ITEM: {
      return {
        ...state,
        listItemsById: state.listItemsById.remove(action.payload.id),
      };
    }

    case ContainerEditActionTypes.CONFIRM_CREATE_RESOURCE_ITEM: {
      const resource = action.payload.resource;
      const defaultOnlineResource = resource.defaultOnlineResource;
      const item: EditableItem = {
        id: action.payload.id,
        type: 'items',
        resource,
        defaultOnlineResource,
      };
      const newItemById = Map({ [item.id]: item });
      const isNewOnlineResource = !isOnlineResourcePreview(
        defaultOnlineResource,
      );
      const newOnlineResourceById = Map(
        isNewOnlineResource ? { [item.id]: defaultOnlineResource } : null,
      );

      return {
        ...state,
        listItemsById: state.listItemsById.merge(newItemById),
        onlineResourcesByItemId: state.onlineResourcesByItemId.merge(
          newOnlineResourceById,
        ),
      };
    }

    case ContainerEditActionTypes.UPDATE_RESOURCE_ITEM_IMPORTANCE: {
      const item: EditableItem = state.listItemsById.get(action.payload.id);
      const updatedItemById = Map({
        [item.id]: { ...item, importance: action.payload.importance },
      });

      return {
        ...state,
        listItemsById: state.listItemsById.merge(updatedItemById),
      };
    }

    case ContainerEditActionTypes.DELETE_RESOURCE_ITEM_IMPORTANCE: {
      const item: EditableItem = state.listItemsById.get(action.payload.id);
      const updatedItemById = Map({ [item.id]: { ...item, importance: null } });

      return {
        ...state,
        listItemsById: state.listItemsById.merge(updatedItemById),
      };
    }

    case ContainerEditActionTypes.CONFIRM_UPDATE_RESOURCE_ITEM_STUDENT_NOTE: {
      const item: EditableItem = state.listItemsById.get(action.payload.id);

      const updatedItemById = Map({
        [item.id]: { ...item, student_note: action.payload.student_note },
      });

      return {
        ...state,
        listItemsById: state.listItemsById.merge(updatedItemById),
      };
    }

    case ContainerEditActionTypes.DELETE_RESOURCE_ITEM_STUDENT_NOTE: {
      const item: EditableItem = state.listItemsById.get(action.payload.id);

      const updatedItemById = Map({
        [item.id]: { ...item, student_note: null },
      });

      return {
        ...state,
        listItemsById: state.listItemsById.merge(updatedItemById),
      };
    }

    case ContainerEditActionTypes.CONFIRM_UPDATE_RESOURCE_ITEM_LIBRARY_NOTE: {
      const item: EditableItem = state.listItemsById.get(action.payload.id);

      const updatedItemById = Map({
        [item.id]: { ...item, library_note: action.payload.library_note },
      });

      return {
        ...state,
        listItemsById: state.listItemsById.merge(updatedItemById),
      };
    }

    case ContainerEditActionTypes.DELETE_RESOURCE_ITEM_LIBRARY_NOTE: {
      const item: EditableItem = state.listItemsById.get(action.payload.id);

      const updatedItemById = Map({
        [item.id]: { ...item, library_note: null },
      });

      return {
        ...state,
        listItemsById: state.listItemsById.merge(updatedItemById),
      };
    }

    case ContainerEditActionTypes.CONFIRM_DELETE_ITEM: {
      const { item } = action.payload;
      return {
        ...state,
        citationsByStyle: state.citationsByStyle.map(byId =>
          byId.remove(item.id),
        ),
        listItemsById: state.listItemsById.remove(item.id),
        onlineResourcesByItemId: state.onlineResourcesByItemId.remove(item.id),
      };
    }

    case ContainerEditActionTypes.CONFIRM_DELETE_SECTION: {
      const { descendantItems } = action.payload;
      return {
        ...state,
        citationsByStyle: state.citationsByStyle.map(byId =>
          byId.removeAll(descendantItems),
        ),
        listItemsById: state.listItemsById.removeAll(descendantItems),
        onlineResourcesByItemId:
          state.onlineResourcesByItemId.removeAll(descendantItems),
      };
    }

    case ListItemActionTypes.ONLINE_RESOURCES_LOADED: {
      const onlineResources = action.payload;

      const onlineResourcesByItemId = onlineResources
        .groupBy(onlineResource => onlineResource.item_id)
        .toMap()
        .map(value => value.last(null))
        .filter(value => (value ? true : false));

      return {
        ...state,
        onlineResourcesByItemId: state.onlineResourcesByItemId.merge(
          onlineResourcesByItemId,
        ),
      };
    }

    case RequestDigitisationActionTypes.COMPLETE_REQUEST_DIGITISATION: {
      const item: EditableItem = state.listItemsById.get(
        action.payload.item.id,
      );

      const updatedItemById = Map({
        [item.id]: {
          ...item,
          content: Object.assign(new DigitisedContent(), {
            status: DigitisationStatus.PENDING,
          }),
        },
      });

      return {
        ...state,
        listItemsById: state.listItemsById.merge(updatedItemById),
      };
    }

    case RequestCourseReserveModalActionTypes.REQUEST_COURSE_RESERVE_SUCCESS: {
      const item: EditableItem = state.listItemsById.get(
        action.payload.listItemId,
      );

      const updatedItemById = Map({
        [item.id]: {
          ...item,
          content: DigitisedContent.create({
            status: DigitisationStatus.REFERRED,
            id: action.payload.courseReserveId,
          }),
        },
      });

      return {
        ...state,
        listItemsById: state.listItemsById.merge(updatedItemById),
      };
    }

    case WithdrawCourseReserveModalActionTypes.WITHDRAW_COURSE_RESERVE_SUCCESS: {
      if (!action.payload.withdrawnListItemId) {
        return state;
      }

      const item: EditableItem = state.listItemsById.get(
        action.payload.withdrawnListItemId,
      );

      const updatedItemById = Map({
        [item.id]: {
          ...item,
          content: DigitisedContent.create({
            ...item.content,
            status: DigitisationStatus.WITHDRAWN,
          }),
        },
      });

      return {
        ...state,
        listItemsById: state.listItemsById.merge(updatedItemById),
      };
    }

    default: {
      return state;
    }
  }
}
